import type { DisplayRequestResponse } from '@bingo/shared';
import { useEffect, useRef, useState } from 'react';
import { qrSvg } from '../../lib/qr.js';

/**
 * Tela de pareamento da TV (R10 do plano).
 *
 * É o primeiro contato com o produto: uma TV ligada, um controle remoto na mão,
 * ninguém com computador. Então a tela faz **uma** coisa — mostra um QR grande
 * e o código — e o celular resolve o resto. Nada aqui exige digitar.
 *
 * A solicitação expira em 10 minutos e a TV **se reanuncia sozinha**: ninguém
 * vai levantar do sofá para recarregar a página.
 */

interface PairingProps {
  request: DisplayRequestResponse | null;
  /** Mensagem de erro ou de token expirado/revogado. */
  message: string | null;
  onRetry: () => void;
}

export function Pairing({ request, message, onRetry }: PairingProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const retryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!request) {
      setSvg(null);
      return;
    }

    let active = true;
    void qrSvg(request.pairingUrl).then((markup) => {
      if (active) setSvg(markup);
    });

    return () => {
      active = false;
    };
  }, [request?.pairingUrl]);

  // Foco no botão quando não há código: numa TV o foco é a única indicação de
  // onde o usuário está, e ele precisa poder apertar OK.
  useEffect(() => {
    if (!request) retryRef.current?.focus();
  }, [request]);

  if (!request) {
    return (
      <div className="pair tv">
        <h1>{message ?? 'Conectando…'}</h1>
        <p>
          {message ? 'Leia o QR code com o celular para conectar de novo.' : 'Preparando o telão.'}
        </p>
        <button type="button" className="tv-btn" ref={retryRef} onClick={onRetry} data-tv-focus>
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <div className="pair tv">
      <h1>Aponte a câmera do celular</h1>

      <div className="qr">
        {svg ? (
          <div dangerouslySetInnerHTML={{ __html: svg }} />
        ) : (
          <div style={{ width: 240, height: 240 }} />
        )}
      </div>

      <div className="code" aria-label={`Código ${request.code.split('').join(' ')}`}>
        {request.code}
      </div>

      <p>
        Ou abra <span className="url">{hostOf(request.pairingUrl)}/parear</span> no celular e digite
        o código.
      </p>

      {message ? <p style={{ color: 'var(--gold)', fontWeight: 700 }}>{message}</p> : null}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
